import React, { Suspense, useEffect } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { I18nextProvider } from "react-i18next";
import { HelmetProvider } from "react-helmet-async";

import i18n from "./utils/i18n";
import getDesignTokens from "./utils/theme";
import Layout from "./components/Layout";
import RequireAuth from "./components/RequireAuth";
import ScrollToTop from "./components/ScrollToTop";
import Backdrop from "./components/common/Backdrop";
import ModalDialog from "./components/common/ModalDialog";
import Script from "./components/Script";
import HelmetJS from "./components/HelmetJS";
import useSyncRootScroll from "./components/useSyncRootScroll";

import Home from "./pages/Home";
import Game from "./pages/Game";
import Bonus from "./pages/Bonus";
import BonusGameArticle from "./pages/Bonus_v1.1";
import PromoCenter from "./pages/PromoCenter";
import RedemptionCode from "./pages/RedemptionCode";
import Affiliate from "./pages/Affiliate";
import Register from "./pages/Register";
import Login from "./pages/Login";
import Vip from "./pages/Vip";
import Vip2 from "./pages/Vip2";
import About from "./pages/About";
import Terms from "./pages/Terms";
import Rules from "./pages/Rules";
import Faq from "./pages/Faq";
import Faq2 from "./pages/Faq2";
import ContactUs from "./pages/ContactUs";
import Language from "./pages/Language";
import HowToJoin from "./pages/HowToJoin";
import HowToDeposit from "./pages/HowToDeposit";
import HowToWithdraw from "./pages/HowToWithdraw";
import ResponsibleGaming from "./pages/ResponsibleGaming";
import KYCVerification from "./pages/KYCVerification";
import AntiScam from "./pages/AntiScam";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import InfoCenter from "./pages/InfoCenter";
import ProfileWallet from "./pages/ProfileWallet";
import Referral from "./pages/Referral";
import Blog from "./pages/Blog";
import BlogDetail from "./pages/BlogDetail";

import GoldenEgg from "./components/GoldenEgg";
import GameLauncher from "./components/Game/GameLauncher";
import PaymentGatewayLauncher from "./components/wallet/PaymentGatewayLauncher";
import GameRedirect from "./components/Game/GameRedirect";
import SpinWheelEmbed from "./components/common/SpinWheelEmbed";

import LiveChatPrefill from "./components/LiveChatPrefill";
import Messaging from "./pages/Messaging";

import { PageContentProvider } from "./contexts/PageContentContext.tsx";
import { MetaTags } from "./components/MetaTags.tsx";

function App() {
  const theme = createTheme(getDesignTokens());
  useSyncRootScroll();
  const location = useLocation();

  useEffect(() => {
    if (!process.env.REACT_APP_LANG_PATH_PARAM) return;

    const DEFAULT_LANG = process.env.REACT_APP_DEFAULT_LANGUAGE;
    const SECOND_LANG = process.env.REACT_APP_SECOND_LANGUAGE;

    const FIRST_LANG_PATH = process.env.REACT_APP_LANG_PATH_FIRSTLANG;
    const SECOND_LANG_PATH = process.env.REACT_APP_LANG_PATH_SECONDLANG;

    const URL_LANG_MAP = {
      [SECOND_LANG_PATH]: SECOND_LANG,
      [FIRST_LANG_PATH]: DEFAULT_LANG,
    };

    let pathname = location.pathname;
    const search = location.search;

    const match = pathname.match(/^\/([^/]+)(\/|$)/);
    const urlLang = match?.[1];

    if (urlLang && URL_LANG_MAP[urlLang]) {
      const detectedLang = URL_LANG_MAP[urlLang];

      if (i18n.language !== detectedLang) {
        i18n.changeLanguage(detectedLang);
      }

      if (localStorage.getItem("language") !== detectedLang) {
        localStorage.setItem("language", detectedLang);
      }
    }
    else {
      const storedLang = localStorage.getItem("language") || DEFAULT_LANG;
      const redirectLang =
        storedLang === SECOND_LANG
          ? SECOND_LANG_PATH
          : FIRST_LANG_PATH;

      window.location.replace(`/${redirectLang}${pathname}${search}`);
      return;
    }

    const langRootMatch = pathname.match(/^\/([^/]+)$/);
    if (langRootMatch) {
      window.history.replaceState(null, "", `${pathname}/${search}`);
      pathname = `${pathname}/`;
    }

    // 4️⃣ REMOVE DOUBLE SLASHES in URL
    const cleanPathname = pathname.replace(/\/{2,}/g, "/");
    if (cleanPathname !== pathname) {
      window.history.replaceState(null, "", `${cleanPathname}${search}`);
    }
  }, [location.pathname]);

  const getRoutePath = (path) => {
    return process.env.REACT_APP_LANG_PATH_PARAM
      ? "/:lang/" + path
      : path;
  };

  const isGameLauncher = location.pathname.includes("gameLauncher");
  if (isGameLauncher) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Routes>
          <Route
            path={getRoutePath("gameLauncher")}
            element={<GameLauncher />}
          />
        </Routes>
      </ThemeProvider>
    );
  }

  const isPaymentGatewayLauncher = location.pathname.includes("paymentGatewayLauncher");
  if (isPaymentGatewayLauncher) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Routes>
          <Route
            path={getRoutePath("paymentGatewayLauncher")}
            element={<PaymentGatewayLauncher />}
          />
        </Routes>
      </ThemeProvider>
    );
  }

  return (
    <HelmetProvider>
      <I18nextProvider i18n={i18n}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <HelmetJS />
            <Script />
            <ScrollToTop />
            <ModalDialog />
            <LiveChatPrefill />

            <PageContentProvider>
              <MetaTags />
              <Suspense fallback={<Backdrop />}>
                <Routes>
                  <Route element={<Layout />}>
                    <Route index element={<Home />} />
                    <Route path="*" element={<Home />} />

                    <Route path={getRoutePath("game/:gameCategoryCode")} element={<Game />} />
                    <Route path={getRoutePath("game/:gameCategoryCode/:gameProviderCode")} element={<Game />} />

                    <Route path={getRoutePath("promotion")} element={<BonusGameArticle />} />
                    <Route path={getRoutePath("promocenter")} element={<PromoCenter />} />
                    <Route path={getRoutePath("affiliate")} element={<Affiliate />} />
                    <Route path={getRoutePath("register")} element={<Register />} />
                    <Route path={getRoutePath("login")} element={<Login />} />
                    <Route path={getRoutePath("vip")} element={<Vip2 />} />
                    <Route path={getRoutePath("about")} element={<About />} />
                    <Route path={getRoutePath("terms")} element={<Terms />} />
                    <Route path={getRoutePath("rules")} element={<Rules />} />
                    <Route
                      path={getRoutePath("faq")}
                      element={process.env.REACT_APP_USE_FAQ2 === "true" ? <Faq2 /> : <Faq />}
                    />
                    <Route path={getRoutePath("contactus")} element={<ContactUs />} />
                    <Route path={getRoutePath("language")} element={<Language />} />
                    <Route path={getRoutePath("howToJoin")} element={<HowToJoin />} />
                    <Route path={getRoutePath("howToDeposit")} element={<HowToDeposit />} />
                    <Route path={getRoutePath("howToWithdraw")} element={<HowToWithdraw />} />
                    <Route path={getRoutePath("responsibleGaming")} element={<ResponsibleGaming />} />
                    <Route path={getRoutePath("kycVerification")} element={<KYCVerification />} />
                    <Route path={getRoutePath("antiScam")} element={<AntiScam />} />
                    <Route path={getRoutePath("forgotPassword")} element={<ForgotPassword />} />
                    <Route path={getRoutePath("resetPassword")} element={<ResetPassword />} />
                    <Route path={getRoutePath("infoCenter")} element={<InfoCenter />} />

                    <Route element={<RequireAuth />}>
                      <Route path={getRoutePath("profileWallet/:tab?")} element={<ProfileWallet />} />
                      <Route path={getRoutePath("messaging")} element={<Messaging />} />
                      {/* Login required - the redeem endpoint is ROLE_PLAYER gated, so a
                          logged-out visitor could read the page but never submit. Also
                          gated on the tenant flag so a direct URL 404s where the feature
                          is off. */}
                      {process.env.REACT_APP_SHOW_REDEMPTIONCODE === "true" && (
                        <Route path={getRoutePath("redemptioncode")} element={<RedemptionCode />} />
                      )}
                    </Route>

                    <Route path={getRoutePath("referral")} element={<Referral />} />
                    {process.env.REACT_APP_SHOW_BLOG === "true" && (
                      <>
                        <Route path={getRoutePath("blog")} element={<Blog />} />
                        <Route path={getRoutePath("blog/:slug")} element={<BlogDetail />} />
                      </>
                    )}
                    <Route path={getRoutePath("goldenegg")} element={<GoldenEgg />} />
                  </Route>

                  <Route path={getRoutePath("gameRedirect")} element={<GameRedirect />} />
                  <Route path={getRoutePath("spinWheel")} element={<SpinWheelEmbed />} />
                </Routes>
              </Suspense>
            </PageContentProvider>
          </ThemeProvider>
        </LocalizationProvider>
      </I18nextProvider>
    </HelmetProvider>
  );
}

export default App;
